import React, { useState, useEffect, useRef, memo } from 'react';
import { Document, CardViewConfig, CardViewField } from '../types';
import { Calendar, DollarSign, Eye, Edit, Trash2, Image as ImageIcon, Building2, RotateCcw, User, Mail, Phone, MapPin, Package, Tag, CreditCard, FileCheck, Clock, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import { apiService } from '../services/apiService';
import { cardViewConfigService } from '../services/cardViewConfigService';
import { useToast } from '../hooks/useToast';
import { getUserFriendlyErrorMessage } from '../utils/errorMessage';

// Icon mapping for lucide-react icons
const iconMap: Record<string, React.ComponentType<any>> = {
  Building2,
  DollarSign,
  Calendar,
  User,
  Mail,
  Phone,
  MapPin,
  Package,
  Tag,
  CreditCard,
  FileCheck,
  Clock,
  CheckCircle,
};

interface DocumentCardProps {
  document: Document;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onRestore?: (id: string) => void;
  showRestore?: boolean;
}

const DocumentCard: React.FC<DocumentCardProps> = memo(({ document, onView, onEdit, onDelete, onRestore, showRestore = false }) => {
  const { showToast } = useToast();
  const [thumbnailUrl, setThumbnailUrl] = useState<string>('');
  const [thumbnailLoading, setThumbnailLoading] = useState<boolean>(true);
  const [thumbnailError, setThumbnailError] = useState<boolean>(false);
  const [cardConfig, setCardConfig] = useState<CardViewConfig | null>(null);

  // const statusColors: Record<string, string> = {
  //   active: 'bg-green-100 text-green-800',
  //   pending: 'bg-yellow-100 text-yellow-800',
  //   deleted: 'bg-red-100 text-red-800',
  //   permanently_deleted: 'bg-red-200 text-red-900',
  // }; // Reserved for future use

  // Load card view configuration
  useEffect(() => {
    const loadConfig = async () => {
      try {
        // Try cached config first for immediate use
        const cached = cardViewConfigService.getCachedCardViewConfig();
        if (cached) {
          setCardConfig(cached);
        }
        
        // Load fresh config
        const config = await cardViewConfigService.getCardViewConfig();
        setCardConfig(config);
      } catch (error) {
        const message = getUserFriendlyErrorMessage(error);
        showToast('warning', message || 'Card display settings could not be loaded. Using defaults.');
        try {
          const defaultConfig = await cardViewConfigService.getCardViewConfig();
          setCardConfig(defaultConfig);
        } catch {
          // Keep existing or null config
        }
      }
    };
    loadConfig();
  }, [showToast]);

  // Helper function to get field value from storedFields or customFields
  const getFieldValue = (field: CardViewField): string | undefined => {
    const fieldNames = [field.fieldName, ...(field.fallbackFieldNames || [])];
    const storedFields = document.storedFields || {};
    const customFields = document.customFields || [];

    // Check storedFields first if type is 'stored'
    if (field.type === 'stored') {
      for (const fieldName of fieldNames) {
        const value = storedFields[fieldName];
        if (value !== undefined && value !== null && value !== '') {
          return value;
        }
      }
    }
    
    // Check customFields (case-insensitive match)
    if (customFields.length > 0) {
      for (const fieldName of fieldNames) {
        const customField = customFields.find(
          cf => cf.name && cf.name.toLowerCase() === fieldName.toLowerCase()
        );
        if (customField && customField.value) {
          return customField.value;
        }
      }
    }
    
    // If type is 'stored' but not found, also check customFields as fallback
    if (field.type === 'stored' && customFields.length > 0) {
      for (const fieldName of fieldNames) {
        const customField = customFields.find(
          cf => cf.name && cf.name.toLowerCase() === fieldName.toLowerCase()
        );
        if (customField && customField.value) {
          return customField.value;
        }
      }
    }
    
    return undefined;
  };

  // Lazy loading with Intersection Observer
  const cardRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before card is visible
      }
    );

    const currentCard = cardRef.current;
    if (currentCard) {
      observer.observe(currentCard);
    }

    return () => {
      if (currentCard) {
        observer.unobserve(currentCard);
      }
    };
  }, []);

  // Load thumbnail only when card is visible
  useEffect(() => {
    if (!isVisible || !document.s3Key) {
      setThumbnailLoading(false);
      return;
    }

    const abortController = new AbortController();

    const loadThumbnail = async () => {
      try {
        setThumbnailLoading(true);
        setThumbnailError(false);
        // Use thumbnail if available (much smaller file size)
        // Skip throttling for thumbnails since they're loaded automatically for card previews
        const url = await apiService.getDocumentUrl(document.id, true, true, abortController.signal);
        if (abortController.signal.aborted) return;
        if (url && url.trim() !== '') {
          setThumbnailUrl(url);
        } else {
          // File not found - handled gracefully, show fallback UI
          setThumbnailError(true);
          setThumbnailUrl(''); // Ensure empty URL
        }
      } catch (error: unknown) {
        if (abortController.signal.aborted) return;
        const isFileNotFound =
          (error as { message?: string })?.message?.includes('not found') ||
          (error as { message?: string })?.message?.includes('NoSuchKey') ||
          (error as { message?: string })?.message?.includes('404') ||
          (error as { message?: string })?.message?.toLowerCase().includes('thumbnail not available') ||
          (error as { message?: string })?.message?.toLowerCase().includes('thumbnail has not been generated');
        if (!isFileNotFound) {
          showToast('error', getUserFriendlyErrorMessage(error));
        }
        setThumbnailError(true);
      } finally {
        if (!abortController.signal.aborted) {
          setThumbnailLoading(false);
        }
      }
    };

    loadThumbnail();
    return () => abortController.abort();
  }, [isVisible, document.id, document.s3Key, showToast]);

  // Format date safely
  const formatDate = (dateStr: string): string => {
    try {
      let dateObj = new Date(dateStr);
      if (!isNaN(dateObj.getTime())) {
        return format(dateObj, 'MMM dd, yyyy');
      }
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        dateObj = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        if (!isNaN(dateObj.getTime())) {
          return format(dateObj, 'MMM dd, yyyy');
        }
      }
      return dateStr;
    } catch (error) {
      return dateStr;
    }
  };

  const isPDF = document.fileType === 'application/pdf' || (document.fileName ?? '').toLowerCase().endsWith('.pdf');
  const showThumbnailSection = cardConfig?.showThumbnail !== false;
  const showStatusBadge = cardConfig?.showStatusBadge !== false;
  const hasVisualThumbnail = showThumbnailSection && !thumbnailLoading && !thumbnailError && !!thumbnailUrl;

  const renderStatusBadge = () => {
    if (!showStatusBadge) return null;
    return (
      <span
        className={`px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm backdrop-blur-sm ${
          document.status === 'active'
            ? 'bg-success-500/90 text-white'
            : document.status === 'pending'
            ? 'bg-warning-500/90 text-white'
            : document.status === 'deleted'
            ? 'bg-danger-500/90 text-white'
            : 'bg-gray-500/90 text-white'
        }`}
      >
        {document.status}
      </span>
    );
  };

  return (
    <div 
      ref={cardRef}
      className="bg-white rounded-xl shadow-soft hover:shadow-large transition-all duration-300 overflow-hidden group border border-gray-100 hover:border-primary-200 animate-fade-in-up"
      role="article"
      aria-label={`Document: ${document.fileName}`}
    >
      {/* Thumbnail Section - Only show if configured */}
      {showThumbnailSection && (
        <div 
          className="relative h-32 bg-gradient-to-br from-gray-50 to-gray-100 cursor-pointer overflow-hidden group/thumb focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2 rounded-t-xl"
          onClick={() => onView(document.id)}
          role="button"
          aria-label={`View document ${document.fileName}`}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onView(document.id);
            }
          }}
        >
          {/* Hover overlay */}
          <div className="absolute inset-0 bg-primary-600/0 group-hover/thumb:bg-primary-600/10 transition-all duration-300 z-10 flex items-center justify-center">
            <Eye className="w-8 h-8 text-white opacity-0 group-hover/thumb:opacity-100 transform scale-0 group-hover/thumb:scale-100 transition-all duration-300" />
          </div>
          
          {thumbnailLoading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
              <div className="relative">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary-200 border-t-primary-600"></div>
              </div>
            </div>
          ) : thumbnailError || !thumbnailUrl ? (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
              <div className="text-center text-gray-400">
                <ImageIcon className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-xs">Preview unavailable</p>
              </div>
            </div>
          ) : (
            <>
              {isPDF ? (
                <iframe
                  src={thumbnailUrl}
                  className="w-full h-full border-0 group-hover/thumb:scale-105 transition-transform duration-500"
                  title={document.fileName}
                  onError={() => setThumbnailError(true)}
                />
              ) : (
                <img
                  src={thumbnailUrl}
                  alt={document.fileName}
                  className="w-full h-full object-cover group-hover/thumb:scale-110 transition-transform duration-500"
                  loading="lazy"
                  onError={() => setThumbnailError(true)}
                />
              )}
              {/* Status badge with animation - Only show if configured and thumbnail is visible */}
              {showStatusBadge && hasVisualThumbnail && (
                <div className="absolute top-2 right-2 z-20">
                  {renderStatusBadge()}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Document Info Section */}
      <div className="p-4">
        <div className="mb-3">
          <div className="flex items-start justify-between space-x-2">
            {cardConfig?.showFileName !== false && (
              <h3
                className="font-semibold text-sm text-gray-900 truncate group-hover:text-primary-600 transition-colors"
                title={document.fileName}
              >
                {document.fileName}
              </h3>
            )}
            {/* When there is no visual thumbnail (including preview unavailable or thumbnails disabled),
                show the status badge next to the title so pending status is always visible */}
            {!hasVisualThumbnail && renderStatusBadge()}
          </div>
          {cardConfig?.showDocumentId !== false && (
            <p className="text-xs text-gray-400 mt-0.5">ID: {document.id}</p>
          )}
        </div>

        {/* Dynamic field display based on configuration */}
        {cardConfig && (
          <div className="space-y-2 mb-4">
            {cardConfig.fields
              .filter(field => field.enabled)
              .sort((a, b) => a.order - b.order)
              .map((field) => {
                const fieldValue = getFieldValue(field);
                if (!fieldValue && (field.fieldName ?? '').toLowerCase() === 'date') {
                  // Special handling for date - use uploadDate as fallback
                  const dateValue = document.uploadDate;
                  const IconComponent = iconMap[field.icon || 'Calendar'] || Calendar;
                  return (
                    <div key={field.id} className="flex items-center text-xs text-gray-600 bg-gray-50 rounded-lg px-2 py-1.5">
                      <IconComponent className="w-3.5 h-3.5 mr-1.5 text-accent-500 flex-shrink-0" />
                      <span className="truncate">{formatDate(dateValue)}</span>
                    </div>
                  );
                }
                if (!fieldValue) return null; // Don't show field if no value found
                
                const IconComponent = iconMap[field.icon || 'Tag'] || Tag;
                const isAmount = (field.fieldName ?? '').toLowerCase().includes('amount') || (field.fieldName ?? '').toLowerCase().includes('total');
                
                return (
                  <div key={field.id} className="flex items-center text-xs text-gray-600 bg-gray-50 rounded-lg px-2 py-1.5">
                    <IconComponent className={`w-3.5 h-3.5 mr-1.5 flex-shrink-0 ${
                      field.icon === 'Building2' ? 'text-primary-500' :
                      field.icon === 'DollarSign' ? 'text-success-500' :
                      field.icon === 'Calendar' ? 'text-accent-500' :
                      'text-gray-500'
                    }`} />
                    <span className="font-medium mr-1 min-w-[50px] truncate">{field.label}:</span>
                    <span className={`truncate ${isAmount ? 'font-semibold text-gray-900' : ''}`}>
                      {fieldValue}
                    </span>
                  </div>
                );
              })}
          </div>
        )}

        {/* Enhanced action buttons */}
        <div className="flex space-x-2 pt-3 border-t border-gray-100" role="toolbar" aria-label="Document actions">
          <button
            onClick={() => onView(document.id)}
            aria-label={`View document ${document.fileName}`}
            className="flex-1 flex items-center justify-center px-3 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 hover:text-primary-700 transition-all duration-200 text-xs font-medium group/btn"
          >
            <Eye className="w-3.5 h-3.5 mr-1.5 group-hover/btn:scale-110 transition-transform" />
            View
          </button>
          {showRestore && onRestore ? (
            <>
              <button
                onClick={() => onRestore(document.id)}
                aria-label={`Restore document ${document.fileName}`}
                className="flex-1 flex items-center justify-center px-3 py-2 bg-success-50 text-success-600 rounded-lg hover:bg-success-100 hover:text-success-700 transition-all duration-200 text-xs font-medium group/btn"
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1.5 group-hover/btn:scale-110 transition-transform" />
                Restore
              </button>
              <button
                onClick={() => onDelete(document.id)}
                aria-label={`Permanently delete document ${document.fileName}`}
                className="px-3 py-2 bg-danger-50 text-danger-600 rounded-lg hover:bg-danger-100 hover:text-danger-700 transition-all duration-200 group/btn"
              >
                <Trash2 className="w-3.5 h-3.5 group-hover/btn:scale-110 transition-transform" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => onEdit(document.id)}
                aria-label={`Edit document ${document.fileName}`}
                className="flex-1 flex items-center justify-center px-3 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 hover:text-gray-900 transition-all duration-200 text-xs font-medium group/btn"
              >
                <Edit className="w-3.5 h-3.5 mr-1.5 group-hover/btn:scale-110 transition-transform" />
                Edit
              </button>
              <button
                onClick={() => onDelete(document.id)}
                aria-label={`Delete document ${document.fileName}`}
                className="px-3 py-2 bg-danger-50 text-danger-600 rounded-lg hover:bg-danger-100 hover:text-danger-700 transition-all duration-200 group/btn"
              >
                <Trash2 className="w-3.5 h-3.5 group-hover/btn:scale-110 transition-transform" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
});

DocumentCard.displayName = 'DocumentCard';

export default DocumentCard;
